<template>
  <mds-layout-grid :class="$style['fees-summary']">
    <mds-row>
      <template v-if="advisoryFeeSetting.includeAdvisoryFee && showAdvisoryFee">
        <mds-col :cols="3">
          <mds-label :text="translate('annualAssetBased')" size="small">
            <mds-microcopy :class="$style['annual-Fee-details']" size="small">{{
              annualFeeAmountText
            }}</mds-microcopy>
          </mds-label>
        </mds-col>
        <mds-col v-if="showAnnualFeeFrequency" :cols="3">
          <mds-label :text="translate('billedFrequency')" size="small">
            <mds-microcopy size="small">{{ annualFeeFrequencyText }}</mds-microcopy>
          </mds-label>
        </mds-col>
        <mds-col v-if="showPayFeeMethod" :cols="3">
          <mds-label :text="translate('billAssetBasedFee')" size="small">
            <mds-microcopy size="small">{{ payAdvisoryFeeMethodText }}</mds-microcopy>
          </mds-label>
        </mds-col>
      </template>
      <template v-if="includeTransactionFee && showTransactionFee">
        <mds-col :cols="3">
          <mds-label :text="translate('frontLoad')" size="small">
            <mds-microcopy size="small">{{ frontLoadText }}</mds-microcopy>
          </mds-label>
        </mds-col>
      </template>
    </mds-row>
  </mds-layout-grid>
</template>

<script>
import MwcVueHelperMixin, { MwcConfigMixin } from 'mwc-vue-component-helper';
import { MdsLayoutGrid, MdsRow, MdsCol } from '@mds/layout-grid';
import MdsLabel from '@mds/label';
import MdsMicrocopy from '@mds/microcopy';
import { frontLoadTypeDataMapping } from './utils/utils';
import { annualFeeChargeFrequencies, payFeeMethods } from './utils/input-options';

import { FEE_TYPES, FRONT_LOAD_TYPES, FRONT_LOAD_AMOUNT_TYPES } from './utils/constants';

const { FLAT } = FEE_TYPES;
const { STANDARD, CUSTOM } = FRONT_LOAD_TYPES;
const { AMOUNT } = FRONT_LOAD_AMOUNT_TYPES;

export default {
  name: 'fees-summary',
  components: { MdsLayoutGrid, MdsRow, MdsCol, MdsLabel, MdsMicrocopy },
  mixins: [MwcVueHelperMixin, MwcConfigMixin],
  props: {
    currencySymbol: {
      type: String,
      default: '$',
    },
    showAdvisoryFee: {
      type: Boolean,
      default: true,
    },
    showAnnualFeeFrequency: {
      type: Boolean,
      default: true,
    },
    showPayFeeMethod: {
      type: Boolean,
      default: true,
    },
    showTransactionFee: {
      type: Boolean,
      default: true,
    },
    advisoryFeeSetting: Object,
    advisoryFeeTableData: Array,
    includeTransactionFee: {
      type: Boolean,
      default: false,
    },
    isEqualSecurityFrontLoad: {
      type: Boolean,
      default: false,
    },
    flagSecurityWithFrontLoad: Object,
  },
  data() {
    return {
      defaultConfig: {
        settings: {
          languageId: this.getComponentConfig().settings.languageId || 'en-US',
        },
      },
    };
  },
  computed: {
    defaultVals() {
      return this.settings?.preference;
    },
    annualFeeAmountText() {
      const {
        advisoryFeeSchedule,
        annualFeeType,
        feeScheduleId,
        isVariousAnnualFeeData,
      } = this.advisoryFeeSetting;
      if (isVariousAnnualFeeData) return this.variesLabel;
      if (annualFeeType === FLAT) {
        return this.getFlatFeeSummaryText();
      }
      if (feeScheduleId === this.defaultVals.feeScheduleId) {
        // click to add
        return `${this.formatter.number(this.defaultVals.annualFeeAmount, 'shortDecimal')}%`;
      }
      if (advisoryFeeSchedule?.isMaxFee) {
        return this.getMaxFeeSummaryText();
      }
      return this.getFeeScheduleSummaryText();
    },
    annualFeeFrequencyText() {
      if (this.advisoryFeeSetting.isVariousFeeFrequency) {
        return this.variesLabel;
      }
      const selectedOption = annualFeeChargeFrequencies.find(
        i => i.value === this.advisoryFeeSetting.annualFeeChargeFrequency
      );
      if (!selectedOption) {
        return '';
      }
      return this.translate(selectedOption.labelKey);
    },
    payAdvisoryFeeMethodText() {
      const selectedOption = payFeeMethods.find(
        i => i.value === this.advisoryFeeSetting.annualFeePayMethod
      );
      if (!selectedOption) {
        return '';
      }
      return this.translate(selectedOption.labelKey);
    },
    frontLoadText() {
      if (!this.isEqualSecurityFrontLoad) {
        return this.variesLabel;
      }
      const { frontLoadFeeType, frontLoadAmount } = this.flagSecurityWithFrontLoad;
      const { frontLoadType, frontLoadAmountType } = frontLoadTypeDataMapping(frontLoadFeeType);
      if (frontLoadType === CUSTOM) {
        const customFrontLoad = this.translate('custom');
        const amount = this.formatter.number(frontLoadAmount, 'shortDecimal');
        return frontLoadAmountType === AMOUNT
          ? `${customFrontLoad} ${this.currencySymbol}${amount}`
          : `${customFrontLoad} ${amount}%`;
      }
      return frontLoadType === STANDARD
        ? this.translate('standard')
        : this.translate('letterOfIntent');
    },
    variesLabel() {
      const labelKey = this.settings.isActualWorkflow
        ? 'variesAcrossInvestments'
        : 'variesAcrossHoldings';
      return this.translate(labelKey);
    },
  },
  methods: {
    getFlatFeeSummaryText() {
      const { annualFeeAmount } = this.advisoryFeeSetting;
      if (this.advisoryFeeTableData[0].fees.annualFeeAmountType === 0) {
        return this.formatter.number(annualFeeAmount, 'shortDecimal');
      }
      return `${this.formatter.number(annualFeeAmount, 'shortDecimal')}%`;
    },
    getMaxFeeSummaryText() {
      const {
        advisoryFeeSchedule,
        isVariousAnnualMaxFee,
        annualMaxFee,
        discount,
      } = this.advisoryFeeSetting;
      const { allowMaxDiscount, name } = advisoryFeeSchedule;
      if (isVariousAnnualMaxFee) {
        return this.variesLabel;
      }
      if (!allowMaxDiscount || Math.abs(discount - 0) < 0.00001) {
        return `${this.translate('maxFee')} ${this.formatter.number(
          annualMaxFee,
          'shortDecimal'
        )}% ${name}`;
      }
      return `${this.formatter.number(annualMaxFee, 'shortDecimal')}%`;
    },
    getFeeScheduleSummaryText() {
      const { advisoryFeeSchedule, discount, isVariousDiscount } = this.advisoryFeeSetting;
      const { allowMaxDiscount, name } = advisoryFeeSchedule ?? {};
      if (isVariousDiscount) {
        return this.variesLabel;
      }
      if (allowMaxDiscount && discount) {
        return `${name} - ${this.formatter.number(discount, 'shortDecimal')}% ${this.translate(
          'discountText'
        )}`;
      }
      return name;
    },
  },
};
</script>

<style lang="scss" module>
@import '~@mds/constants';
.fees-summary {
  padding: 0 $mds-space-2-x;
  margin-top: $mds-space-1-x;
  .annual-Fee-details {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}
</style>
